// src/marketing/ContactPage.js - Contact Page
import React, { useState } from 'react';
import {
  Mail,
  Phone,
  MessageSquare,
  MapPin,
  Send,
  CheckCircle,
} from 'lucide-react';

const ContactPage = () => {

  const [formState, setFormState] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    message: '',
    interest: 'general',
  });

  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formError, setFormError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormState((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setFormError(null);

    // Basic validation
    if (!formState.name || !formState.email || !formState.message) {
      setFormError('Please fill in all required fields.');
      setIsLoading(false);
      return;
    }

    // In a real app, you would send this to your serverless function
    // For this demo, we'll just simulate a successful submission
    try {
      // Simulate API call with a delay
      await new Promise((resolve) => setTimeout(resolve, 1000));

      // Successful submission
      setFormSubmitted(true);
      setFormState({
        name: '',
        email: '',
        company: '',
        phone: '',
        message: '',
        interest: 'general',
      });
    } catch (error) {
      setFormError('Something went wrong. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
      <div className="min-h-screen bg-gradient-to-b from-white to-blue-50">
        {/* Contact Section */}
        <section className="pb-16 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="bg-white overflow-hidden shadow-xl rounded-lg">
              <div className="grid grid-cols-1 lg:grid-cols-2">
                {/* Contact Info */}
                <div className="bg-blue-600 text-white p-8 lg:p-12">
                  <h2 className="text-2xl font-bold mb-6">
                    Contact Information
                  </h2>
                  <p className="mb-8 text-blue-100">
                    Fill out the form or reach out to us directly using the
                    contact information below.
                  </p>

                  <div className="space-y-6">
                    <div className="flex items-start">
                      <Mail className="h-6 w-6 text-blue-200 mt-1" />
                      <div className="ml-4">
                        <p className="font-medium">Email</p>
                        <a
                          href="mailto:contact@trackersheets.com"
                          className="text-blue-100 hover:text-white"
                        >
                          contact@trackersheets.com
                        </a>
                      </div>
                    </div>

                    <div className="flex items-start">
                      <Phone className="h-6 w-6 text-blue-200 mt-1" />
                      <div className="ml-4">
                        <p className="font-medium">Phone</p>
                        <a
                          href="tel:+15551234567"
                          className="text-blue-100 hover:text-white"
                        >
                          (555) 123-4567
                        </a>
                      </div>
                    </div>

                    <div className="flex items-start">
                      <MessageSquare className="h-6 w-6 text-blue-200 mt-1" />
                      <div className="ml-4">
                        <p className="font-medium">Live Chat</p>
                        <p className="text-blue-100">
                          Available weekdays 9 AM - 5 PM EST
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start">
                      <MapPin className="h-6 w-6 text-blue-200 mt-1" />
                      <div className="ml-4">
                        <p className="font-medium">Office</p>
                        <p className="text-blue-100">
                          123 Main Street, Suite 400
                          <br />
                          Minneapolis, MN 55401
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="mt-12">
                    <h3 className="font-medium text-lg mb-4">
                      Connect with us
                    </h3>
                    <div className="flex space-x-4">
                      {['twitter', 'facebook', 'linkedin', 'github'].map(
                        (platform) => (
                          <a
                            key={platform}
                            href={`https://${platform}.com`}
                            className="h-10 w-10 rounded-full bg-blue-500 flex items-center justify-center hover:bg-blue-400 transition-colors"
                          >
                            <span className="sr-only">{platform}</span>
                            <svg
                              className="h-5 w-5 text-white"
                              fill="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path d="M12 0c-6.627 0-12 5.373-12 12s5.373 12 12 12 12-5.373 12-12-5.373-12-12-12zm-2 16h-2v-6h2v6zm-1-6.891c-.607 0-1.1-.496-1.1-1.109 0-.612.492-1.109 1.1-1.109s1.1.497 1.1 1.109c0 .613-.493 1.109-1.1 1.109zm8 6.891h-1.998v-2.861c0-1.881-2.002-1.722-2.002 0v2.861h-2v-6h2v1.093c.872-1.616 4-1.736 4 1.548v3.359z" />
                            </svg>
                          </a>
                        )
                      )}
                    </div>
                  </div>
                </div>

                {/* Contact Form */}
                <div className="p-8 lg:p-12">
                  {formSubmitted ? (
                    <div className="h-full flex flex-col items-center justify-center text-center">
                      <div className="rounded-full bg-green-100 p-3 mb-4">
                        <CheckCircle className="h-8 w-8 text-green-600" />
                      </div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-2">
                        Thank You!
                      </h3>
                      <p className="text-gray-600 mb-6">
                        Your message has been received. We'll get back to you as
                        soon as possible.
                      </p>
                      <button
                        onClick={() => setFormSubmitted(false)}
                        className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                      >
                        Send Another Message
                      </button>
                    </div>
                  ) : (
                    <>
                      <h2 className="text-2xl font-bold text-gray-900 mb-6">
                        Send us a message
                      </h2>

                      {formError && (
                        <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 text-red-700">
                          <p>{formError}</p>
                        </div>
                      )}

                      <form onSubmit={handleSubmit} className="space-y-6">
                        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                          <div>
                            <label
                              htmlFor="name"
                              className="block text-sm font-medium text-gray-700 mb-1"
                            >
                              Name <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              id="name"
                              name="name"
                              value={formState.name}
                              onChange={handleChange}
                              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 border p-2"
                              required
                            />
                          </div>

                          <div>
                            <label
                              htmlFor="email"
                              className="block text-sm font-medium text-gray-700 mb-1"
                            >
                              Email <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="email"
                              id="email"
                              name="email"
                              value={formState.email}
                              onChange={handleChange}
                              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 border p-2"
                              required
                            />
                          </div>

                          <div>
                            <label
                              htmlFor="company"
                              className="block text-sm font-medium text-gray-700 mb-1"
                            >
                              Company
                            </label>
                            <input
                              type="text"
                              id="company"
                              name="company"
                              value={formState.company}
                              onChange={handleChange}
                              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 border p-2"
                            />
                          </div>

                          <div>
                            <label
                              htmlFor="phone"
                              className="block text-sm font-medium text-gray-700 mb-1"
                            >
                              Phone
                            </label>
                            <input
                              type="tel"
                              id="phone"
                              name="phone"
                              value={formState.phone}
                              onChange={handleChange}
                              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 border p-2"
                            />
                          </div>
                        </div>

                        <div>
                          <label
                            htmlFor="interest"
                            className="block text-sm font-medium text-gray-700 mb-1"
                          >
                            I'm interested in:
                          </label>
                          <select
                            id="interest"
                            name="interest"
                            value={formState.interest}
                            onChange={handleChange}
                            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 border p-2"
                          >
                            <option value="general">General Information</option>
                            <option value="demo">Product Demo</option>
                            <option value="pricing">Pricing Questions</option>
                            <option value="enterprise">
                              Enterprise Package
                            </option>
                            <option value="support">Technical Support</option>
                          </select>
                        </div>

                        <div>
                          <label
                            htmlFor="message"
                            className="block text-sm font-medium text-gray-700 mb-1"
                          >
                            Message <span className="text-red-500">*</span>
                          </label>
                          <textarea
                            id="message"
                            name="message"
                            rows={5}
                            value={formState.message}
                            onChange={handleChange}
                            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 border p-2"
                            required
                          />
                        </div>

                        <div>
                          <button
                            type="submit"
                            disabled={isLoading}
                            className="inline-flex items-center justify-center w-full py-3 px-6 border border-transparent rounded-md shadow-sm text-base font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                          >
                            {isLoading ? (
                              <>
                                <svg
                                  className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                                  xmlns="http://www.w3.org/2000/svg"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                >
                                  <circle
                                    className="opacity-25"
                                    cx="12"
                                    cy="12"
                                    r="10"
                                    stroke="currentColor"
                                    strokeWidth="4"
                                  ></circle>
                                  <path
                                    className="opacity-75"
                                    fill="currentColor"
                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                  ></path>
                                </svg>
                                Sending...
                              </>
                            ) : (
                              <>
                                <Send className="mr-2 h-5 w-5" />
                                Send Message
                              </>
                            )}
                          </button>
                        </div>
                      </form>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Map Section */}
        <section className="py-12 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-extrabold text-gray-900">
                Visit Our Office
              </h2>
              <p className="mt-4 text-lg text-gray-500">
                Drop by for a coffee and a chat about how we can help your
                business.
              </p>
            </div>

            <div className="h-96 w-full bg-gray-200 rounded-lg overflow-hidden shadow-md">
              {/* In a real app, this would be a Google Maps embed */}
              <div className="h-full w-full flex items-center justify-center bg-gray-200">
                <div className="text-gray-500 text-center">
                  <MapPin className="h-12 w-12 mx-auto mb-2 text-gray-400" />
                  <p className="text-xl font-medium">Map Placeholder</p>
                  <p>123 Main Street, Suite 400, Minneapolis, MN 55401</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-12 bg-gray-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-extrabold text-gray-900">
                Frequently Asked Questions
              </h2>
              <p className="mt-4 text-lg text-gray-500">
                Can't find what you're looking for? Reach out to our team.
              </p>
            </div>

            <div className="space-y-6">
              {[
                {
                  question: 'How quickly will I receive a response?',
                  answer:
                    'We typically respond to all inquiries within 24 business hours. For urgent matters, please indicate in your message or call us directly.',
                },
                {
                  question: 'Can I schedule a product demo?',
                  answer:
                    "Absolutely! Select 'Product Demo' in the form, and we'll reach out to schedule a personalized demonstration at a time that works for you.",
                },
                {
                  question: 'Do you offer customer support for all plans?',
                  answer:
                    'Yes, all plans include customer support. The level of support varies by plan - Starter includes email support, Professional adds chat support, and Business includes priority support.',
                },
              ].map((faq, index) => (
                <div key={index} className="bg-white rounded-lg shadow-sm p-6">
                  <h3 className="text-lg font-medium text-gray-900">
                    {faq.question}
                  </h3>
                  <p className="mt-2 text-gray-600">{faq.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>
  );
};

export default ContactPage;
